/**
 * Экран кошелька: балансы, ступень долга, суточная сводка, журнал (§15, §Т2.4).
 */

import { formatCrystals, formatDuration, formatNumber, formatTime, type LocaleCode } from "@hive/i18n";
import { useTranslation } from "react-i18next";

import type { EconomySummaryView, LedgerEntryView, ServerWalletView } from "../api/wallet.js";
import type { WalletView } from "../game/economy.js";

import { ConvertPanel } from "./ConvertPanel.jsx";
import { CrystalIcon, Crystals } from "./CrystalIcon.jsx";
import { LedgerList } from "./LedgerList.jsx";
import "./money/money.css";

export interface WalletScreenProps {
  readonly locale: LocaleCode;
  readonly timeZone: string;
  readonly wallet: WalletView;
  readonly walletServer: ServerWalletView;
  readonly summary: EconomySummaryView | null;
  readonly ledger: readonly LedgerEntryView[];
  readonly ledgerLoading?: boolean;
  readonly onLoadMoreLedger?: () => void;
  readonly ledgerHasMore?: boolean;
  readonly onOpenDebts?: () => void;
  readonly onOpenSettings?: () => void;
  readonly onReportLedgerEntry?: (entryId: number) => void;
  readonly convertBusy?: boolean;
  readonly onConvert?: (amount: number) => Promise<void>;
}

export function WalletScreen({
  locale,
  timeZone,
  wallet,
  walletServer,
  summary,
  ledger,
  ledgerLoading = false,
  onLoadMoreLedger,
  ledgerHasMore = false,
  onOpenDebts,
  onOpenSettings,
  onReportLedgerEntry,
  convertBusy = false,
  onConvert,
}: WalletScreenProps): React.JSX.Element {
  const { t } = useTranslation(["economy", "common", "support"]);

  const ecoEnds = summary ? new Date(summary.ecoDayEndsAt) : null;
  const ecoRemaining =
    ecoEnds !== null ? Math.max(0, ecoEnds.getTime() - Date.now()) : null;

  return (
    <div className="screen screen--wallet">
      <header className="screen__head screen__head--row">
        <h1 className="screen__title">{t("economy:wallet.title")}</h1>
        {onOpenSettings && (
          <button type="button" className="button" onClick={onOpenSettings}>
            {t("support:settings.title")}
          </button>
        )}
      </header>

      <section className="wallet-panel" aria-label={t("economy:wallet.title")}>
        <div className="wallet-panel__grid">
          <div className="wallet-panel__item wallet-panel__item--hive">
            <span className="wallet-panel__coin">
              <CrystalIcon kind="hive" size={44} />
            </span>
            <span className="wallet-panel__label">{t("economy:wallet.hive")}</span>
            <span className="wallet-panel__value">
              {formatNumber(locale, Math.floor(wallet.hive / 1000))}
            </span>
            <span className="wallet-panel__hint">{t("economy:wallet.hive.hint")}</span>
          </div>

          <div className="wallet-panel__item wallet-panel__item--cash">
            <span className="wallet-panel__coin">
              <CrystalIcon kind="cash" size={44} />
            </span>
            <span className="wallet-panel__label">{t("economy:wallet.cash")}</span>
            <span className="wallet-panel__value">
              {formatNumber(locale, Math.floor(wallet.cash / 1000))}
            </span>
            <span className="wallet-panel__hint">{t("economy:wallet.cash.hint")}</span>
          </div>
        </div>

        {wallet.held > 0 && (
          <p className="wallet-panel__note">
            <Crystals>
              {t("economy:wallet.held", {
                amount: formatCrystals(locale, wallet.held, "hive"),
              })}
            </Crystals>
          </p>
        )}

        <p className="wallet-panel__effective">
          <Crystals>
            {t("economy:wallet.effective", {
              amount: formatCrystals(locale, wallet.effective, "hive"),
            })}
          </Crystals>
        </p>

        {walletServer.debt > 0 && (
          <div className="wallet-panel__debt">
            <p className="wallet-panel__debt-amount">
              <Crystals>
                {t("economy:debt.amount", {
                  amount: formatCrystals(locale, wallet.debt, "hive"),
                })}
              </Crystals>
            </p>
            <p className="wallet-panel__tier">{t(`economy:debt.tier.${wallet.tier}`)}</p>
            {walletServer.recoveryDaysLeft > 0 && (
              <p className="wallet-panel__recovery">
                {t("economy:debt.recovery_days", { count: walletServer.recoveryDaysLeft })}
              </p>
            )}
            {onOpenDebts && (
              <button type="button" className="button button--link" onClick={onOpenDebts}>
                {t("economy:debt.open_list")}
              </button>
            )}
          </div>
        )}
      </section>

      {onConvert && (
        <ConvertPanel
          locale={locale}
          cashCrystals={Math.floor(wallet.cash / 1000)}
          busy={convertBusy}
          onConvert={onConvert}
        />
      )}

      {summary && (
        <section className="summary" aria-label={t("economy:summary.title")}>
          <h2 className="screen__section-title">{t("economy:summary.title")}</h2>
          {ecoRemaining !== null && ecoEnds !== null && (
            <p className="summary__eco">
              {t("economy:summary.eco_reset", {
                time: formatTime(locale, ecoEnds, timeZone),
                duration: formatDuration(locale, ecoRemaining),
              })}
            </p>
          )}
          <dl className="summary__facts">
            <div className="summary__fact summary__fact--in">
              <dt>{t("economy:summary.income_hex")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.incomeHex * 1000, "hive")}</Crystals>
              </dd>
            </div>
            <div className="summary__fact summary__fact--in">
              <dt>{t("economy:summary.income_hearth")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.incomeHearth * 1000, "hive")}</Crystals>
              </dd>
            </div>
            <div className="summary__fact summary__fact--in">
              <dt>{t("economy:summary.income_lodgers")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.incomeLodgers * 1000, "hive")}</Crystals>
              </dd>
            </div>
            <div className="summary__fact summary__fact--out">
              <dt>{t("economy:summary.tribute_paid")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.tributePaid * 1000, "hive")}</Crystals>
              </dd>
            </div>
            <div className="summary__fact summary__fact--in">
              <dt>{t("economy:summary.tribute_received")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.tributeReceived * 1000, "hive")}</Crystals>
              </dd>
            </div>
            <div className="summary__fact">
              <dt>{t("economy:summary.tribute_cap_left")}</dt>
              <dd>
                <Crystals>{formatCrystals(locale, summary.tributeCapLeft * 1000, "hive")}</Crystals>
              </dd>
            </div>
          </dl>
        </section>
      )}

      <LedgerList
        locale={locale}
        timeZone={timeZone}
        entries={ledger}
        loading={ledgerLoading}
        hasMore={ledgerHasMore}
        {...(onLoadMoreLedger ? { onLoadMore: onLoadMoreLedger } : {})}
        {...(onReportLedgerEntry ? { onReportEntry: onReportLedgerEntry } : {})}
      />
    </div>
  );
}
